import React, { useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { useFinanceStore } from '../../store/useFinanceStore';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, TrendingDown } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import {
  classifyGoalMovement,
  resolveGoalCategoryContext,
  GOAL_CATEGORY_FALLBACK,
  GOAL_SUBCATEGORY_ICON,
  extractGoalDescriptor,
} from '../../utils/goalContribution';

ChartJS.register(ArcElement, Tooltip, Legend);

interface DoughnutChartProps {
  title: string;
}

const DoughnutChart: React.FC<DoughnutChartProps> = ({ title }) => {
  const { transactions, categoriesWithSubs } = useFinanceStore();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [expandedCategoryId, setExpandedCategoryId] = useState<string | null>(null);
  const [filterMode, setFilterMode] = useState<'both' | 'expense' | 'income'>('both');

  // Calcular lançamentos por categoria (todas as transações não canceladas)
  const expensesByCategory = useMemo(() => {
    const { goalCategory, goalCategoryIds } = resolveGoalCategoryContext(categoriesWithSubs);
    const goalCategoryVisual = {
      id: goalCategory?.id ?? GOAL_CATEGORY_FALLBACK.id,
      name: goalCategory?.name ?? GOAL_CATEGORY_FALLBACK.name,
      icon: goalCategory?.icon ?? GOAL_CATEGORY_FALLBACK.icon,
      color: goalCategory?.color ?? GOAL_CATEGORY_FALLBACK.color,
    };

      // Agora consideramos os lançamentos conforme o filtro selecionado (todos/despesa/receita)
      const relevantTransactions = transactions.filter((t) =>
        t.status !== 'cancelled' && (filterMode === 'both' || t.type === filterMode)
      );

    const contributionTotalsByGoal = new Map<string, { name: string; total: number }>();
    const refundTotalsByGoal = new Map<string, number>();

    const categoryMap = new Map<string, { 
      name: string; 
      icon: string; 
      color: string; 
      total: number;
      subcategories: Map<string, { name: string; icon: string; total: number }>;
    }>();

    relevantTransactions.forEach((transaction) => {
      const movementType = classifyGoalMovement(transaction, goalCategoryIds);

      if (movementType === 'contribution') {
        const absoluteAmount = Math.abs(transaction.amount);
        if (absoluteAmount === 0) {
          return;
        }

        const descriptor = extractGoalDescriptor(transaction);
        const contribution = contributionTotalsByGoal.get(descriptor.key);

        if (contribution) {
          contribution.total += absoluteAmount;
        } else {
          contributionTotalsByGoal.set(descriptor.key, {
            name: descriptor.name,
            total: absoluteAmount,
          });
        }
        return;
      }

      // Usar category_id se disponível, senão mapear category antiga
      let categoryId = transaction.category_id;
      
      // Fallback: mapear categorias antigas para novas
      if (!categoryId && transaction.category) {
        const categoryName = transaction.category;
        const foundCategory = categoriesWithSubs
          .flatMap(c => [c, ...c.subcategories])
          .find(c => c.name === categoryName);
        categoryId = foundCategory?.id;
      }

      // 🆕 CATEGORIAS PERSONALIZADAS: Criar categoria virtual
      if (!categoryId && transaction.customCategoryName) {
        const CUSTOM_CATEGORY_VIRTUAL = {
          id: 'custom-virtual-id',
          name: 'Categorias Personalizadas',
          icon: '✨',
          color: '#a78bfa',
        };

        // Adicionar/atualizar categoria virtual
        if (!categoryMap.has(CUSTOM_CATEGORY_VIRTUAL.id)) {
          categoryMap.set(CUSTOM_CATEGORY_VIRTUAL.id, {
            name: CUSTOM_CATEGORY_VIRTUAL.name,
            icon: CUSTOM_CATEGORY_VIRTUAL.icon,
            color: CUSTOM_CATEGORY_VIRTUAL.color,
            total: 0,
            subcategories: new Map()
          });
        }

        const catData = categoryMap.get(CUSTOM_CATEGORY_VIRTUAL.id)!;
        const absoluteAmount = Math.abs(transaction.amount);
        catData.total += absoluteAmount;

        // Subcategoria = nome da categoria personalizada
        const subKey = transaction.customCategoryName;
        if (!catData.subcategories.has(subKey)) {
          catData.subcategories.set(subKey, {
            name: transaction.customCategoryName,
            icon: transaction.customSubcategoryName ? '📋' : '📝',
            total: 0
          });
        }
        const subData = catData.subcategories.get(subKey)!;
        subData.total += absoluteAmount;
        
        // Se tiver subcategoria personalizada, adicionar detalhe
        if (transaction.customSubcategoryName) {
          const subSubKey = `${subKey}-${transaction.customSubcategoryName}`;
          if (!catData.subcategories.has(subSubKey)) {
            catData.subcategories.set(subSubKey, {
              name: `  ↳ ${transaction.customSubcategoryName}`,
              icon: '  └─',
              total: 0
            });
          }
          const subSubData = catData.subcategories.get(subSubKey)!;
          subSubData.total += absoluteAmount;
        }
        
        return; // Processado como categoria custom
      }

      if (!categoryId) return;

      // Encontrar categoria e sua principal
      let mainCategory;
      let subcategory;

      for (const main of categoriesWithSubs) {
        if (main.id === categoryId) {
          mainCategory = main;
          break;
        }
        const sub = main.subcategories.find(s => s.id === categoryId);
        if (sub) {
          mainCategory = main;
          subcategory = sub;
          break;
        }
      }

      if (!mainCategory) return;

      // Adicionar/atualizar categoria principal
      if (!categoryMap.has(mainCategory.id)) {
        categoryMap.set(mainCategory.id, {
          name: mainCategory.name,
          icon: mainCategory.icon,
          color: mainCategory.color,
          total: 0,
          subcategories: new Map()
        });
      }

      const catData = categoryMap.get(mainCategory.id)!;
      catData.total += Math.abs(transaction.amount);

      // Adicionar/atualizar subcategoria se existir
      if (subcategory) {
        if (!catData.subcategories.has(subcategory.id)) {
          catData.subcategories.set(subcategory.id, {
            name: subcategory.name,
            icon: subcategory.icon,
            total: 0
          });
        }
        const subData = catData.subcategories.get(subcategory.id)!;
        subData.total += Math.abs(transaction.amount);
      }
    });
    transactions.forEach((transaction) => {
      if (transaction.status === 'cancelled') {
        return;
      }

      const movementType = classifyGoalMovement(transaction, goalCategoryIds);
      if (movementType !== 'refund') {
        return;
      }

      const absoluteAmount = Math.abs(transaction.amount);
      if (absoluteAmount === 0) {
        return;
      }

      const descriptor = extractGoalDescriptor(transaction);
      const currentRefund = refundTotalsByGoal.get(descriptor.key) ?? 0;
      refundTotalsByGoal.set(descriptor.key, currentRefund + absoluteAmount);

      if (!contributionTotalsByGoal.has(descriptor.key)) {
        contributionTotalsByGoal.set(descriptor.key, {
          name: descriptor.name,
          total: 0,
        });
      }
    });

    const goalSubcategories = new Map<string, { name: string; icon: string; total: number }>();
    let goalCategoryNetTotal = 0;

    contributionTotalsByGoal.forEach((entry, key) => {
      const refund = refundTotalsByGoal.get(key) ?? 0;
      const net = Math.max(0, entry.total - refund);

      if (net > 0) {
        goalCategoryNetTotal += net;
        goalSubcategories.set(key, {
          name: entry.name,
          icon: GOAL_SUBCATEGORY_ICON,
          total: net,
        });
      }
    });

    if (goalSubcategories.size > 0) {
      const orderedSubcategories = new Map(
        Array.from(goalSubcategories.entries()).sort((a, b) =>
          a[1].name.localeCompare(b[1].name, 'pt-BR')
        )
      );

      categoryMap.set(goalCategoryVisual.id, {
        name: goalCategoryVisual.name,
        icon: goalCategoryVisual.icon,
        color: goalCategoryVisual.color,
        total: goalCategoryNetTotal,
        subcategories: orderedSubcategories,
      });
    }

    return Array.from(categoryMap.entries())
      .map(([id, data]) => ({
        id,
        ...data,
        subcategories: Array.from(data.subcategories.values()).filter((sub) => sub.total > 0)
      }))
      .filter((category) => category.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [transactions, categoriesWithSubs, filterMode]);

  // Preparar dados para o gráfico donut
  const chartData = useMemo(() => {
    const labels = expensesByCategory.map(c => `${c.icon} ${c.name}`);
    const values = expensesByCategory.map(c => c.total);
    const colors = expensesByCategory.map(c => c.color);

    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: colors,
          borderColor: colors.map(c => c),
          borderWidth: 2,
          hoverBorderWidth: 3,
          hoverBorderColor: '#ffffff'
        },
      ],
    };
  }, [expensesByCategory]);

  const tooltipBackground = isDark ? '#1e293b' : '#ffffff';
  const tooltipTitleColor = isDark ? '#f1f5f9' : '#0f172a';
  const tooltipBodyColor = isDark ? '#f1f5f9' : '#0f172a';
  const tooltipBorderColor = isDark ? '#334155' : '#5eead4';

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false // Esconder legenda padrão, vamos criar customizada
      },
      tooltip: {
        backgroundColor: tooltipBackground,
        titleColor: tooltipTitleColor,
        bodyColor: tooltipBodyColor,
        borderColor: tooltipBorderColor,
        borderWidth: 1,
        callbacks: {
          label: function(context: any) {
            const value = context.parsed;
            const label = context.label;
            return `${label}: R$ ${value.toFixed(2)}`;
          },
        },
      },
    },
    onClick: (_event: any, elements: any) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const categoryId = expensesByCategory[index].id;
        setExpandedCategoryId(expandedCategoryId === categoryId ? null : categoryId);
      }
    }
  };

  const totalExpenses = expensesByCategory.reduce((sum, c) => sum + c.total, 0);

  // Calcular máximo para normalizar barras
  const maxSubcategoryValue = useMemo(() => {
    let max = 0;
    expensesByCategory.forEach(cat => {
      cat.subcategories.forEach(sub => {
        if (sub.total > max) max = sub.total;
      });
    });
    return max || 1;
  }, [expensesByCategory]);

  if (expensesByCategory.length === 0) {
    const emptyMessage =
      filterMode === 'both'
        ? 'Nenhum lançamento encontrado'
        : filterMode === 'expense'
        ? 'Nenhuma despesa encontrada'
        : 'Nenhuma receita encontrada';

    return (
      <div
        className={`rounded-xl p-6 border transition-colors ${
          isDark
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-teal-200 shadow-sm shadow-[0_10px_30px_rgba(45,212,191,0.15)]'
        }`}
      >
        <div className="flex items-center gap-3 mb-6">
          <div
            className={`p-2 rounded-lg ${
              isDark ? 'bg-purple-500/20' : 'bg-purple-100'
            }`}
          >
            <TrendingDown className={isDark ? 'text-purple-400' : 'text-purple-600'} size={20} />
          </div>
          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {title}
          </h3>
        </div>
        <div
          className={`flex items-center justify-center h-48 ${
            isDark ? 'text-slate-500' : 'text-slate-600'
          }`}
        >
          {emptyMessage}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-xl p-6 border transition-colors ${
        isDark
          ? 'bg-slate-900 border-slate-700'
          : 'bg-white border-teal-200 shadow-lg shadow-[0_18px_45px_rgba(45,212,191,0.18)]'
      }`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div
          className={`p-2 rounded-lg ${
            isDark ? 'bg-purple-500/20' : 'bg-purple-100'
          }`}
        >
          <TrendingDown className={isDark ? 'text-purple-400' : 'text-purple-600'} size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {title}
          </h3>
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Total: R$ {totalExpenses.toFixed(2)}
          </p>
        </div>

        {/* Toggle: Todos / Despesas / Receitas */}
        <div className="flex gap-2 items-center">
          <button
            onClick={() => setFilterMode('both')}
            className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${
              filterMode === 'both'
                ? isDark
                  ? 'bg-slate-700 text-white border border-slate-600'
                  : 'bg-teal-500 text-white'
                : isDark
                  ? 'bg-slate-800 text-slate-300 border border-slate-700'
                  : 'bg-white text-slate-700 border border-teal-100'
            }`}
            aria-pressed={filterMode === 'both'}
            aria-label="Mostrar todos os lançamentos"
          >
            Todos
          </button>
          <button
            onClick={() => setFilterMode('expense')}
            className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${
              filterMode === 'expense'
                ? isDark
                  ? 'bg-rose-700 text-white border border-rose-600'
                  : 'bg-rose-500 text-white'
                : isDark
                  ? 'bg-slate-800 text-slate-300 border border-slate-700'
                  : 'bg-white text-slate-700 border border-teal-100'
            }`}
            aria-pressed={filterMode === 'expense'}
            aria-label="Mostrar apenas despesas"
          >
            Despesas
          </button>
          <button
            onClick={() => setFilterMode('income')}
            className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${
              filterMode === 'income'
                ? isDark
                  ? 'bg-emerald-700 text-white border border-emerald-600'
                  : 'bg-emerald-500 text-white'
                : isDark
                  ? 'bg-slate-800 text-slate-300 border border-slate-700'
                  : 'bg-white text-slate-700 border border-teal-100'
            }`}
            aria-pressed={filterMode === 'income'}
            aria-label="Mostrar apenas receitas"
          >
            Receitas
          </button>
        </div>
      </div>

      {/* Layout Híbrido: Donut + Barras Laterais */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Coluna Esquerda: Gráfico Donut */}
        <div className="h-80 relative flex flex-col">
          <div className="flex-1">
            <Doughnut data={chartData} options={chartOptions} />
          </div>

          {/* Instrução - mais visível */}
          <p className={`text-sm font-medium mt-4 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
            💡 Clique em uma categoria para ver as subcategorias
          </p>
        </div>
        {/* Coluna Direita: Legenda Customizada + Barras Expansíveis */}
        <div
          className={`h-80 overflow-y-auto pr-2 space-y-2 scrollbar-thin ${
            isDark
              ? 'scrollbar-thumb-slate-600 scrollbar-track-slate-800/50'
              : 'scrollbar-thumb-teal-200 scrollbar-track-teal-50'
          }`}
        >
          {expensesByCategory.map((category) => {
            const percentage = (category.total / totalExpenses) * 100;
            const isExpanded = expandedCategoryId === category.id;
            const hasSubcategories = category.subcategories.length > 0;

            return (
              <div key={category.id} className="space-y-2">
                {/* Categoria Principal - Clique para expandir subcategorias */}
                <button
                  onClick={() => hasSubcategories && setExpandedCategoryId(isExpanded ? null : category.id)}
                  className={`w-full px-4 py-3 rounded-lg border-l-4 transition-all duration-200 ${
                    isExpanded
                      ? isDark
                        ? 'bg-slate-700 shadow-lg'
                        : 'bg-teal-100 shadow-md'
                      : isDark
                        ? 'bg-slate-800 hover:bg-slate-750'
                        : 'bg-white hover:bg-teal-50 shadow-sm'
                  } ${hasSubcategories ? 'cursor-pointer' : 'cursor-default'}`}
                  style={{ borderLeftColor: category.color }}
                  disabled={!hasSubcategories}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="text-xl flex-shrink-0">{category.icon}</span>
                      <span
                        className={`text-sm font-medium truncate ${
                          isDark ? 'text-slate-200' : 'text-slate-700'
                        }`}
                      >
                        {category.name}
                      </span>
                      {hasSubcategories && (
                        <ChevronRight 
                          size={16}
                          className={`transition-transform flex-shrink-0 ${
                            isDark ? 'text-slate-400' : 'text-teal-500'
                          } ${isExpanded ? 'rotate-90' : ''}`}
                        />
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                      <span
                        className={`text-xs font-medium ${
                          isDark ? 'text-slate-400' : 'text-slate-500'
                        }`}
                      >
                        {percentage.toFixed(1)}%
                      </span>
                      <span
                        className={`text-sm font-semibold min-w-[80px] text-right ${
                          isDark ? 'text-slate-200' : 'text-slate-700'
                        }`}
                      >
                        R$ {category.total.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </button>

                {/* Subcategorias Expansíveis - Itens da categoria */}
                <AnimatePresence>
                  {isExpanded && hasSubcategories && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="pl-6 pr-2 space-y-2 overflow-hidden"
                    >
                      {category.subcategories.map((sub) => {
                        const subPercentage = (sub.total / category.total) * 100;
                        const barWidth = (sub.total / maxSubcategoryValue) * 100;

                        return (
                          <div
                            key={sub.name}
                            className={`flex items-center gap-2 py-2 px-3 rounded-lg border transition-colors ${
                              isDark
                                ? 'bg-slate-800/70 border border-slate-700/50 hover:bg-slate-700/50'
                                : 'bg-white border border-teal-100 hover:bg-teal-50'
                            }`}
                          >
                            <span className="text-lg flex-shrink-0">{sub.icon}</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1.5">
                                <span
                                  className={`text-xs font-medium truncate ${
                                    isDark ? 'text-slate-300' : 'text-slate-600'
                                  }`}
                                >
                                  {sub.name}
                                </span>
                                <span
                                  className={`text-xs ml-2 flex-shrink-0 ${
                                    isDark ? 'text-slate-400' : 'text-slate-500'
                                  }`}
                                >
                                  {subPercentage.toFixed(0)}%
                                </span>
                              </div>
                              {/* Barra Horizontal */}
                              <div
                                className={`h-2 rounded-full overflow-hidden ${
                                  isDark ? 'bg-slate-700/50' : 'bg-teal-100'
                                }`}
                              >
                                <motion.div
                                  initial={{ width: 0 }}
                                  animate={{ width: `${barWidth}%` }}
                                  transition={{ duration: 0.5, delay: 0.1 }}
                                  className="h-full rounded-full"
                                  style={{ backgroundColor: category.color }}
                                />
                              </div>
                            </div>
                            <span
                              className={`text-xs font-semibold ml-2 whitespace-nowrap flex-shrink-0 min-w-[70px] text-right ${
                                isDark ? 'text-slate-200' : 'text-slate-700'
                              }`}
                            >
                              R$ {sub.total.toFixed(2)}
                            </span>
                          </div>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DoughnutChart;